/**
 * The PixelWrangler colour codes list (BLU-1399, CC-1, CC-2): six numbered
 * steps for the PixelWrangler template, then one item per colour with its code
 * and a Copy button (the shared `CopyValue`). Rendered by the Product sync
 * Customizer section and by the Custom files step after a saved key.
 *
 * Reads `onboarding.getCustomizerColours` with the SAME input and options as
 * the section header, so it reuses that cache entry: opening the section adds
 * no colour request. 60 s cache, no focus refetch, no poll. Disabled, and
 * nothing rendered, for a role without `org_settings.read` (the step embed).
 *
 * New and Done (CC-3): a synced colour first seen after the org's last Done
 * press carries a New badge. Viewing never clears it; `Done, I added these`
 * writes one ack and then patches the shared cache entry (cancel, then
 * `setData`), which also clears the section header's chip. Never `invalidate`
 * or `refetch`: Retry's refetch is the only one.
 *
 * Lives in `features/onboarding/components/custom-files/` and imports nothing
 * from `features/product-sync` (N11). Copy is in the `productSync` namespace,
 * which `lib/i18n.ts` loads statically, so it resolves on any page.
 */
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useRole } from '@/hooks/use-role';
import { cn } from '@/lib/utils';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { CopyValue } from '@/features/organization/components/shopify-connection/CopyValue';
import { CUSTOMIZER_COLOURS_QUERY_OPTIONS } from './customizer-query';

type CustomizerColoursView = RouterOutputs['onboarding']['getCustomizerColours'];
type ConnectedView = Extract<CustomizerColoursView, { connected: true }>;

const STEP_KEYS = [
  'customizer.step1',
  'customizer.step2',
  'customizer.step3',
  'customizer.step4',
  'customizer.step5',
  'customizer.step6',
] as const;

export function CustomizerColourCodes({ showSetupLink = true }: { showSetupLink?: boolean }) {
  const { t } = useTranslation('productSync');
  const { can } = useRole();
  const allowed = can('org_settings.read');
  // Same input and options as the section header (one cache entry), plus the permission gate.
  const query = trpc.onboarding.getCustomizerColours.useQuery(undefined, {
    ...CUSTOMIZER_COLOURS_QUERY_OPTIONS,
    enabled: allowed,
  });

  if (!allowed) return null;

  if (query.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">{t('customizer.loadError')}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
          {t('states.retry')}
        </Button>
      </div>
    );
  }

  if (!query.data.connected) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">{t('customizer.notConnected')}</p>
        {showSetupLink ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/onboarding">{t('customizer.openSetup')}</Link>
          </Button>
        ) : null}
      </div>
    );
  }

  return <ConnectedList view={query.data} />;
}

function SourceNote({ view }: { view: ConnectedView }) {
  const { t } = useTranslation('productSync');
  const lines: string[] = [];
  switch (view.source) {
    case 'synced':
      break;
    case 'threePl':
      lines.push(t('customizer.syncFirst'), t('customizer.fromThreePl', { names: view.threePlNames.join(', ') }));
      break;
    case 'own':
      lines.push(t('customizer.syncFirst'), t('customizer.fromOwn'));
      break;
    case 'ownDeclared':
      // S4: a store SKU org's rows never sync to this list, so no "sync first".
      lines.push(t('customizer.fromOwnDeclared'));
      break;
    case 'none':
      lines.push(t('customizer.syncFirst'));
      break;
  }
  if (lines.length === 0) return null;
  return (
    <div className="space-y-1">
      {lines.map((line) => (
        <p key={line} className="text-sm text-muted-foreground">
          {line}
        </p>
      ))}
    </div>
  );
}

function ConnectedList({ view }: { view: ConnectedView }) {
  const { t } = useTranslation('productSync');
  const stepsId = useId();
  const listId = useId();
  const utils = trpc.useUtils();
  const [doneStatus, setDoneStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const acknowledge = trpc.onboarding.acknowledgeCustomizerColours.useMutation({
    onSuccess: async () => {
      // Patch, never refetch: cancel an in-flight read first so it cannot land over the patch.
      await utils.onboarding.getCustomizerColours.cancel();
      utils.onboarding.getCustomizerColours.setData(undefined, (prev) =>
        prev && prev.connected ? { ...prev, colours: prev.colours.map((c) => ({ ...c, isNew: false })) } : prev,
      );
      setDoneStatus('saved');
    },
    onError: () => setDoneStatus('error'),
  });

  const synced = view.source === 'synced';
  const seenThrough = view.seenThrough;
  const showDone = synced && seenThrough !== null && view.colours.some((c) => c.isNew);

  return (
    <div className="space-y-4">
      <p className="max-w-prose text-sm text-muted-foreground">{t('customizer.description')}</p>

      <div className="space-y-1.5">
        <p id={stepsId} className="text-sm font-medium text-foreground">
          {t('customizer.stepsLabel')}
        </p>
        <ol aria-labelledby={stepsId} className="max-w-prose list-decimal space-y-1 pl-5 text-sm text-foreground">
          {STEP_KEYS.map((key) => (
            <li key={key}>{t(key)}</li>
          ))}
        </ol>
      </div>

      <SourceNote view={view} />

      {view.colours.length > 0 ? (
        <div className="space-y-1.5">
          <p id={listId} className="text-sm font-medium text-foreground">
            {t('customizer.listLabel')}
          </p>
          <ul aria-labelledby={listId} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {view.colours.map((colour) => (
              <li key={`${colour.code}-${colour.name ?? ''}`} className="min-w-0">
                <CopyValue
                  label={colour.name ?? t('customizer.unnamed', { code: colour.code })}
                  value={colour.code}
                  badge={
                    synced && colour.isNew ? (
                      <Badge variant="default" size="sm">
                        {t('customizer.new')}
                      </Badge>
                    ) : undefined
                  }
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showDone ? (
        <div className="space-y-3">
          <p className="max-w-prose text-sm text-muted-foreground">{t('customizer.newNote')}</p>
          <Button
            type="button"
            size="sm"
            disabled={acknowledge.isPending}
            onClick={() => {
              if (seenThrough === null) return;
              setDoneStatus('idle');
              acknowledge.mutate({ seenThrough });
            }}
          >
            {t('customizer.done')}
          </Button>
        </div>
      ) : null}

      {synced ? (
        <p
          role="status"
          aria-live="polite"
          className={cn('text-sm', doneStatus === 'error' ? 'text-destructive' : 'text-muted-foreground')}
        >
          {doneStatus === 'saved' ? t('customizer.doneSaved') : doneStatus === 'error' ? t('customizer.doneError') : ''}
        </p>
      ) : null}
    </div>
  );
}
